import {
  Text,
  Box,
  Flex,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  HStack,
  MenuItem as ChakraMenuItem,
  useDisclosure,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { ChevronsLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ApplicationPaths } from "@/components/authorization/AuthorizationConstants";
import { useAuth } from "@/context/AuthProvider";
import GuestBookingDetailsFormHOC from "@/pages/MealBooking/Form/GuestBookingDetailsForm";
import FeedbackFormHOC from "@/pages/MealBooking/Form/FeedbackForm";
import { useRecentMealServings } from "@/services/useMealServing";
import { withModalForm } from "@ui/components/forms";
import useIsMobile from "@ui/hooks/useIsMobile";
import { useSelf } from "@ui/services/useUser";
import { IUser } from "@/services/useProfile";
import { useAdhocRecentServings } from "@/services/useAdhocServing";
import MealConsumedFormHOC from "@/pages/MealBooking/Form/MealConsumedForm";
import { IEmployee, useEmployeeSelf } from "@/services/useEmployee";
import { DateTime } from "luxon";

const GuestBookingDetailsForm = withModalForm(GuestBookingDetailsFormHOC);
const FeedbackForm = withModalForm(FeedbackFormHOC);
const MealConsumedForm = withModalForm(MealConsumedFormHOC);

type IProps = {
  variant?: "supplier-portal" | "default";
};

const UserProfile = ({ variant = "default" }: IProps) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isSupplierPortal = variant === "supplier-portal";
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenFeedback,
    onOpen: onOpenFeedback,
    onClose: onCloseFeedback,
  } = useDisclosure();
  const {
    isOpen: isMealConsumedOpen,
    onOpen: onOpenMealConsumed,
    onClose: onCloseMealConsumed,
  } = useDisclosure();

  const { data: me } = useSelf<IUser>();
  const {
    data: recentServings,
    isPending,
    isLoading,
  } = useRecentMealServings(me?.id, {
    enabled: isOpenFeedback && !!me?.id,
  });
  const {
    data: adhocRecentServings,
    isPending: isPendingServings,
    isLoading: isLoadingServings,
  } = useAdhocRecentServings(me?.id, {
    enabled: isOpenFeedback && !!me?.id,
  });
  const { data: employee } = useEmployeeSelf<IEmployee>();
  const today = DateTime.now().toISO();
  const lastWorkingDate = employee?.lastWorkingDate
    ? typeof employee.lastWorkingDate === "string"
      ? DateTime.fromISO(employee.lastWorkingDate).toISO()
      : employee.lastWorkingDate.toISO?.()
    : undefined;
  const isTerminated =
    employee?.status?.label?.toLowerCase() === "terminated" ||
    (!!lastWorkingDate && lastWorkingDate <= today);

  const isMobile = useIsMobile();

  return (
    <>
      <Menu>
        <MenuButton>
          <Flex alignItems="center" gap={2}>
            <Avatar name={me?.name} cursor="pointer" size={"sm"} />
            <Text
              color={"#172B4D"}
              fontSize={"14px"}
              fontWeight={"normal"}
              flex="1 1 auto"
              noOfLines={1}
              textAlign="left"
            >
              {me?.name}
            </Text>
            <Box display="none" width={6} height={6}>
              <ChevronsLeft size="20px" color="#42526E" strokeWidth="1.33" />
            </Box>
          </Flex>
        </MenuButton>
        <MenuList zIndex={1000}>
          <HStack p={2}>
            <Avatar name={me?.name} size="sm" />
            <Stack spacing={0}>
              <Text fontWeight="medium">{me?.name}</Text>
            </Stack>
          </HStack>
          {!isTerminated && (
            <>
              {!isSupplierPortal && (
                <>
                  <ChakraMenuItem onClick={onOpen}>
                    Guest Details
                  </ChakraMenuItem>
                  <ChakraMenuItem onClick={onOpenFeedback}>
                    Feedback
                  </ChakraMenuItem>
                </>
              )}
              {!isMobile && !isSupplierPortal && (
                <ChakraMenuItem onClick={onOpenMealConsumed}>
                  Meal Consumed Summary
                </ChakraMenuItem>
              )}
            </>
          )}
          <ChakraMenuItem
            as={ChakraLink}
            href="https://www.machdatum.com/support/cms"
            isExternal
          >
            Support
          </ChakraMenuItem>
          {isAuthenticated ? (
            <ChakraMenuItem
              onClick={() =>
                navigate(ApplicationPaths.LogOut, { state: { local: true } })
              }
            >
              Logout
            </ChakraMenuItem>
          ) : (
            <ChakraMenuItem
              onClick={() =>
                navigate(ApplicationPaths.Login, { state: { local: true } })
              }
            >
              Login
            </ChakraMenuItem>
          )}
        </MenuList>
      </Menu>
      <GuestBookingDetailsForm isOpen={isOpen} size="2xl" onClose={onClose} />
      {isOpenFeedback && (
        <FeedbackForm
          isOpen={isOpenFeedback}
          size="2xl"
          mealServedData={recentServings ?? []}
          adhocMealServedData={adhocRecentServings ?? []}
          onClose={onCloseFeedback}
          isLoading={
            isPending || isLoading || isPendingServings || isLoadingServings
          }
        />
      )}
      {isMealConsumedOpen && (
        <MealConsumedForm
          isOpen={isMealConsumedOpen}
          size="2xl"
          onClose={onCloseMealConsumed}
        />
      )}
    </>
  );
};

export default UserProfile;
